import { computed } from 'vue';

// Tipos de status de missão
export type MissionStatus =
  | 'claimable' // Pode resgatar prêmio
  | 'optIn' // Precisa aceitar participação
  | 'participating' // Já está participando
  | 'completed' // Concluída (prêmio já resgatado ou não requer resgate)
  | 'expired' // Expirada
  | 'locked'; // Bloqueada/Indisponível

// Função utilitária pura para determinar o status da missão
// Pode ser usada fora de componentes Vue (ex: na store)
export function getMissionStatus(mission: TMissionOrBadge): MissionStatus {
  const currentTime = new Date().getTime();

  // Verifica se expirou por time_limit_ms (missão com tempo limitado após início)
  const isExpiredByTimeLimit =
    mission.dt_start &&
    mission.time_limit_ms &&
    currentTime > mission.dt_start + mission.time_limit_ms;

  // Verifica se expirou por active_till_ts (data de fim da missão)
  const isExpiredByActiveTill =
    mission.active_till_ts && currentTime > mission.active_till_ts;

  const isExpired = isExpiredByTimeLimit || isExpiredByActiveTill;

  // 1. Bloqueada
  if (mission.is_locked) {
    return 'locked';
  }

  // 2. Expirada (antes de verificar completed para não mostrar expiradas que foram completadas)
  if (isExpired && !mission.is_completed) {
    return 'expired';
  }

  // 3. Pode resgatar prêmio
  if (
    mission.is_completed &&
    mission.requires_prize_claim &&
    !mission.prize_claimed_date_ts
  ) {
    return 'claimable';
  }

  // 4. Concluída (prêmio já resgatado ou não requer resgate)
  if (mission.is_completed) {
    return 'completed';
  }

  // 5. Precisa aceitar participação (opt-in)
  if (!mission.is_opted_in && mission.is_requires_optin) {
    return 'optIn';
  }

  // 6. Já está participando
  return 'participating';
}

// Função para obter o rank numérico (para ordenação)
export function getMissionStatusRank(mission: TMissionOrBadge): number {
  const status = getMissionStatus(mission);
  const rankMap: Record<MissionStatus, number> = {
    claimable: 1,
    optIn: 2,
    participating: 3,
    locked: 4,
    completed: 5,
    expired: 6,
  };

  return rankMap[status];
}

export const useMissionButton = (props: { mission: TMissionOrBadge }) => {
  const mission = computed(() => props.mission);
  const authStore = useAuthStore();
  const buttonStyles = {
    optinBtn:
      'flex h-[22px] sm:h-[32px] justify-center items-center gap-[5px] md:gap-[7px] border-[0.7px] md:border-[1px] border-solid border-[#fc9984] [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] px-[7px] md:px-[10px] py-0 rounded-[4px] md:rounded-[6px] cursor-pointer transition-all duration-300 hover:brightness-95',
    alreadyParticipatingBtn:
      'flex h-[47px] justify-center items-center gap-[7px] border [background:rgba(23,201,100,0.10)] px-2.5 py-0 rounded-md border-solid border-[rgba(23,201,100,0.50)] cursor-pointer transition-all duration-300 hover:brightness-110',
    claimBtn:
      'flex h-[26px] md:h-[40px] justify-center items-center gap-[6px] md:gap-[9px] border md:border-[1.5px] border-solid border-[#26b3ec] [background:rgba(20,20,221,0.2)] px-[11px] md:px-[18px] py-0 rounded-[6px] md:rounded-[9px] cursor-pointer transition-all duration-300 hover:brightness-95',
    expiredBtn:
      'cursor-not-allowed flex h-[47px] justify-center items-center gap-2.5 border [background:rgba(77,45,0,0.50)] backdrop-blur-[10px] px-4 py-3 rounded-[10px] border-solid border-[rgba(255,184,84,0.50)] transition-all duration-300 hover:brightness-110',
    claimedBtn:
      'flex h-[26px] md:h-[40px] justify-center items-center gap-[3px] md:gap-[5px] [background:rgba(23,201,100,0.2)] pl-[9px] md:pl-[14px] pr-[11px] md:pr-[18px] py-0 rounded-[6px] md:rounded-[9px] cursor-not-allowed transition-all duration-300',
    lockedBtn:
      'flex h-[47px] justify-center items-center gap-[7px] px-2.5 py-0 rounded-md cursor-not-allowed transition-all duration-300 hover:brightness-110',
  };

  const buttonTextStyles = {
    optinBtn:
      'text-white text-center text-[8.4px] md:text-[12.5px] font-bold leading-[14px] md:leading-[21px]',
    alreadyParticipatingBtn:
      'text-[#17C964] text-[13px] font-extrabold leading-4',
    claimBtn:
      'text-[#00c9ff] text-center text-[8.6px] md:text-[13px] font-bold leading-[14px] md:leading-[22px]',
    expiredBtn: 'text-[#FF9D00] text-[13px] font-extrabold leading-4',
    claimedBtn:
      'text-[#81FFA1] text-center text-[8.6px] md:text-[13px] font-extrabold leading-[14px] md:leading-[22px]',
    lockedBtn:
      'text-[color:var(--White,#FFF)] text-center text-[15px] font-extrabold leading-3',
  };

  const iconMap: Record<MissionStatus, string> = {
    locked: 'mission/button/indisponivel',
    expired: 'mission/button/expirado',
    claimable: '',
    completed: 'mission/button/resgatado',
    optIn: 'mission/button/participar',
    participating: 'mission/button/participar',
  };

  const statusTextMap: Record<MissionStatus, string> = {
    locked: 'Aguardando',
    expired: 'Expirada',
    claimable: 'Ativa',
    completed: 'Concluída',
    optIn: 'Aguardando',
    participating: 'Ativa',
  };

  // Status reativo baseado na função pura
  // Quando deslogado, "participating" deve aparecer como "optIn" (disponível)
  const missionStatus = computed(() => {
    const status = getMissionStatus(mission.value);

    // Se deslogado e status é "participating", trata como "optIn"
    if (!authStore.isAuthenticated && status === 'participating') {
      return 'optIn';
    }

    return status;
  });

  // Computed properties derivadas do status (para compatibilidade)
  const isExpired = computed(() => missionStatus.value === 'expired');
  const isClaimable = computed(() => missionStatus.value === 'claimable');
  const isOptInRequired = computed(() => missionStatus.value === 'optIn');
  const isAlreadyParticipating = computed(
    () => missionStatus.value === 'participating',
  );

  const isCompleted = computed(() => missionStatus.value === 'completed');
  const isLocked = computed(() => missionStatus.value === 'locked');

  const buttonState = computed(() => {
    const status = missionStatus.value;

    const stateMap: Record<
      MissionStatus,
      {
        text: string;
        class: string;
        textClass: string;
        action: string | null;
        disabled: boolean;
      }
    > = {
      claimable: {
        text: 'Resgatar',
        class: buttonStyles.claimBtn,
        textClass: buttonTextStyles.claimBtn,
        action: 'claimReward',
        disabled: false,
      },
      optIn: {
        text: 'Ver missão',
        class: buttonStyles.optinBtn,
        textClass: buttonTextStyles.optinBtn,
        action: 'optIn',
        disabled: false,
      },
      participating: {
        text: 'Participando',
        class: buttonStyles.alreadyParticipatingBtn,
        textClass: buttonTextStyles.alreadyParticipatingBtn,
        action: null,
        disabled: false,
      },
      locked: {
        text: 'Indisponível',
        class: buttonStyles.lockedBtn,
        textClass: buttonTextStyles.lockedBtn,
        action: null,
        disabled: true,
      },
      completed: {
        text: 'Resgatado',
        class: buttonStyles.claimedBtn,
        textClass: buttonTextStyles.claimedBtn,
        action: null,
        disabled: true,
      },
      expired: {
        text: 'Expirado',
        class: buttonStyles.expiredBtn,
        textClass: buttonTextStyles.expiredBtn,
        action: null,
        disabled: true,
      },
    };

    const state = stateMap[status];
    const iconName = iconMap[status];
    const participationText = statusTextMap[status];
    const textColorClass =
      state.textClass
        .split(' ')
        .find((c) => c.startsWith('text-[') || c.startsWith('text-')) || '';

    return {
      ...state,
      iconName,
      participationText,
      textColorClass,
    };
  });

  return {
    missionStatus,
    buttonState,
    isClaimable,
    isOptInRequired,
    isAlreadyParticipating,
    isLocked,
    isCompleted,
    isExpired,
  };
};
